import React from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import GradientIconBadge from './GradientIconBadge';
import { gradients, glassPanel } from '../../themes/gradients';

/**
 * Cabeçalho "hero" padrão de toda página do sistema (lista, dashboard,
 * relatório) - painel de vidro com 2 manchas de gradiente desfocadas ao
 * fundo (as "camadas sobrepostas"/profundidade pedidas), badge de ícone
 * em gradiente, título e um slot de ações à direita (botão "Novo X" etc).
 */
const PageHeader = ({ icon, gradient = 'blue', title, subtitle, actions }) => {
    const theme = useTheme();
    const g = gradients[gradient] || gradients.blue;
    const isDark = theme.palette.mode === 'dark';

    return (
        <Box
            component={motion.div}
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
            sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '24px',
                p: { xs: 2.5, md: 3.5 },
                mb: 3,
                ...glassPanel(theme)
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    width: 260,
                    height: 260,
                    borderRadius: '50%',
                    background: g.full,
                    opacity: isDark ? 0.22 : 0.28,
                    filter: 'blur(70px)',
                    top: -120,
                    right: -60,
                    pointerEvents: 'none'
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    width: 180,
                    height: 180,
                    borderRadius: '50%',
                    background: gradients.teal.full,
                    opacity: isDark ? 0.14 : 0.16,
                    filter: 'blur(60px)',
                    bottom: -90,
                    left: '30%',
                    pointerEvents: 'none'
                }}
            />

            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={2}
                sx={{ position: 'relative', zIndex: 1 }}
            >
                <Stack direction="row" spacing={2} alignItems="center">
                    {icon && <GradientIconBadge icon={icon} gradient={gradient} size={54} />}
                    <Box>
                        <Typography variant="h3" fontWeight={800} sx={{ letterSpacing: '-0.02em' }}>
                            {title}
                        </Typography>
                        {subtitle && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                </Stack>

                {actions && (
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        {actions}
                    </Stack>
                )}
            </Stack>
        </Box>
    );
};

PageHeader.propTypes = {
    icon: PropTypes.elementType,
    gradient: PropTypes.oneOf(['blue', 'teal', 'amber', 'coral', 'graphite']),
    title: PropTypes.node,
    subtitle: PropTypes.node,
    actions: PropTypes.node
};

export default PageHeader;
